import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import Typography from '@material-ui/core/Typography'
import DeleteOutlined from '@material-ui/icons/DeleteOutlined'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import {
    useCKEditorToggle,
    useContentItemViewNode,
} from 'components/WorkspaceEditor/hooks'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import type { DataNode } from 'components/WorkspaceEditor/types'
import React, { Dispatch, memo, SetStateAction, useState } from 'react'
import { ContentEditorProps } from '../../ContentEditor'
import { withLazyLoad } from '../../withLazyComponent'
import { useSpecifierNotesController } from './useSpecifierNotesController'

/** Props for the SpecifierNotes component */
export interface SpecifierNotesProps {
    /** The node that has the specifier notes to render */
    node: DataNode
    /** A flag whether the node is an article note specifier */
    isArticleNoteSpecifier?: boolean
    /** A flag whether the node is editable */
    editable?: boolean
    /** Classes to style the component */
    classes?: string
    /** Data test id for testing purposes */
    dataTestId?: string
}

export interface NoteProps {
    node: Pick<DataNode, 'key' | 'specifierNotes' | 'parentComments'>
    noteId: number
    noteState: [string, Dispatch<SetStateAction<string>>]
    isArticleNoteSpecifier?: boolean
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

const Note = memo<NoteProps>(
    ({ node, noteId, noteState, isArticleNoteSpecifier }) => {
        const { note, handlers } = useSpecifierNotesController({
            node,
            noteId,
            noteState,
            isArticleNoteSpecifier,
        })

        return <ContentEditor initialData={note} handlers={handlers} />
    }
)

/**
 * A editable component that renders specifier notes.
 * @param {SpecifierNotesProps} props - Props for the component.
 */
export const SpecifierNotes = memo<SpecifierNotesProps>(
    ({
        node,
        isArticleNoteSpecifier = false,
        editable = false,
        classes,
        dataTestId,
    }) => {
        const noteState = useState('')
        const {
            specifierNoteContainer,
            specifierNoteDeleteIcon,
            readOnlyCursor,
        } = useTreeViewStyles()

        const { deleteSpecifierNote } = useContentItemViewNode()
        const isContentTeamFromStudioPage = isReactiveContentTeamFromStudioPage()
            ?.value
        const isDeleteHidden = node.isDeactivated
        const { activeEditorKey, placeholderRef, activate } = useCKEditorToggle(
            'specifier-note-0'
        )
        const notes =
            (isArticleNoteSpecifier
                ? node.parentComments
                : node.specifierNotes) || []

        return (
            <>
                {notes.map((note, index) => {
                    if (note?.trim() === '') return null

                    const formatContentId = `specifier-note-${index}`
                    const isEditorActive =
                        // - the node is editable (isInProject)
                        editable &&
                        // - not deactivated
                        !isDeleteHidden &&
                        // - the current note is active OR if there is a note in the state..
                        (activeEditorKey === formatContentId || noteState[0]) &&
                        // - editing from OM or SMM page
                        isContentTeamFromStudioPage

                    return (
                        <Typography
                            key={formatContentId}
                            className={clsx(
                                classes,
                                specifierNoteContainer,
                                !isContentTeamFromStudioPage && readOnlyCursor
                            )}
                            data-testid={dataTestId}
                            tabIndex={0}
                            ref={isEditorActive ? placeholderRef : null}
                            onFocusCapture={() => activate(formatContentId)}
                        >
                            {isEditorActive ? (
                                <Note
                                    data-testid="specifier-note-item"
                                    node={node}
                                    noteId={index}
                                    noteState={noteState}
                                    isArticleNoteSpecifier={
                                        isArticleNoteSpecifier
                                    }
                                />
                            ) : (
                                note
                            )}

                            {isContentTeamFromStudioPage && !isDeleteHidden && (
                                <HoverTooltip
                                    text="Delete Note"
                                    placements={['top']}
                                >
                                    <DeleteOutlined
                                        data-testid="removeNoteIcon"
                                        tabIndex={0}
                                        className={specifierNoteDeleteIcon}
                                        aria-label="Remove note"
                                        onClick={() =>
                                            deleteSpecifierNote(
                                                node,
                                                index,
                                                isArticleNoteSpecifier
                                            )
                                        }
                                    />
                                </HoverTooltip>
                            )}
                        </Typography>
                    )
                })}
            </>
        )
    }
)
